import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { nxObjectRepositorySingleton } from '@warehouse/object-browser/infra';

interface UseCreateFolderParams {
	currentBucketPath: string;
	bucketInfo: {
		id: string;
		isManaged: boolean;
	};
}

interface CreateFolderDialogState {
	open: boolean;
	folderName: string;
	validationError: string | null;
}

export function useCreateFolder({ currentBucketPath, bucketInfo }: UseCreateFolderParams) {
	const [isCreating, setIsCreating] = useState(false);
	const [dialogState, setDialogState] = useState<CreateFolderDialogState>({
		open: false,
		folderName: '',
		validationError: null,
	});
	const { enqueueSnackbar } = useSnackbar();
	const nxObjectRepository = nxObjectRepositorySingleton.get();

	const validateFolderName = useCallback((folderName: string): string | null => {
		if (!folderName.trim()) {
			return 'Folder name cannot be empty';
		}
		if (folderName.includes('/')) {
			return 'Folder name cannot contain forward slash (/)';
		}
		return null;
	}, []);

	const handleOpenDialog = useCallback(() => {
		setDialogState({
			open: true,
			folderName: '',
			validationError: null,
		});
	}, []);

	const handleCloseDialog = useCallback(() => {
		setDialogState({
			open: false,
			folderName: '',
			validationError: null,
		});
	}, []);

	const handleFolderNameChange = useCallback(
		(folderName: string) => {
			const validationError = validateFolderName(folderName);
			setDialogState((prev) => ({
				...prev,
				folderName,
				validationError,
			}));
		},
		[validateFolderName],
	);

	const handleCreateFolder = useCallback(async () => {
		if (!bucketInfo.isManaged) {
			enqueueSnackbar('Folder creation is only available for managed buckets', { variant: 'error' });
			return;
		}

		const validationError = validateFolderName(dialogState.folderName);
		if (validationError) {
			setDialogState((prev) => ({
				...prev,
				validationError,
			}));
			return;
		}

		setIsCreating(true);
		try {
			const response = await nxObjectRepository.createFolder({
				folderName: dialogState.folderName.trim(),
				bucket: bucketInfo.id,
				parentPath: currentBucketPath || undefined,
			});

			enqueueSnackbar(`Folder "${dialogState.folderName}" created successfully at ${response.folderKey}`, {
				variant: 'success',
			});
			handleCloseDialog();
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Failed to create folder';
			enqueueSnackbar(errorMessage, { variant: 'error' });
		} finally {
			setIsCreating(false);
		}
	}, [
		bucketInfo,
		currentBucketPath,
		dialogState.folderName,
		validateFolderName,
		nxObjectRepository,
		enqueueSnackbar,
		handleCloseDialog,
	]);

	return {
		// Dialog state
		showDialog: dialogState.open,
		folderName: dialogState.folderName,
		validationError: dialogState.validationError,

		// Actions
		handleOpenDialog,
		handleCloseDialog,
		handleFolderNameChange,
		handleCreateFolder,

		// Loading state
		isCreating,
	};
}
